import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { apiService } from '../../../../services/api';
import { Empenho, SolicitacaoCompra } from '../../../../types/empenhos';

interface FormAutorizarEmpenhoProps {
  empenho: Empenho;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const IMPOSTO_FIXO = 0.09;
const MARGEM_MINIMA = 15;

const calcularMargem = (valorProposta: number, custo: number): number => {
  if (!custo || custo <= 0 || valorProposta <= 0) return 0;
  const imposto = valorProposta * IMPOSTO_FIXO;
  return ((valorProposta - custo - imposto) / valorProposta) * 100;
};

const formatarValor = (valor: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);

const formatarData = (data: string): string => {
  if (!data) return '-';
  const parts = data.split('T')[0].split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : data;
};

const FormAutorizarEmpenho: React.FC<FormAutorizarEmpenhoProps> = ({
  empenho,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [solicitacao, setSolicitacao] = useState<SolicitacaoCompra | null>(null);
  const [parcelas, setParcelas] = useState<any[]>([]);

  // Modo ativo: null = seleção, 'autorizar' = confirmação, 'negar' = campo de motivo
  const [modo, setModo] = useState<null | 'autorizar' | 'negar'>(null);
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    if (isOpen && empenho.id_solicitacao_compra) {
      setIsLoading(true);
      setSolicitacao(null);
      setParcelas([]);
      setModo(null);
      setMotivo('');

      Promise.all([
        apiService.getSolicitacaoCompra(Number(empenho.id_solicitacao_compra)),
        apiService.getParcelasEmpenho(empenho.id),
      ])
        .then(([scResp, parcelasResp]) => {
          if (scResp.success && scResp.data) setSolicitacao(scResp.data);
          if (parcelasResp.success && parcelasResp.data) {
            setParcelas(parcelasResp.data.filter((p: any) => p.situacao !== 'CANCELADA'));
          }
        })
        .catch(() => toast.error('Erro ao carregar detalhes da solicitação'))
        .finally(() => setIsLoading(false));
    }
  }, [isOpen, empenho.id_solicitacao_compra]);

  const handleAutorizar = async () => {
    setIsSubmitting(true);
    try {
      const response = await apiService.avancarEtapaEmpenho({
        id_empenho: empenho.id,
        dados: {},
      } as any);
      if (response.success) {
        toast.success('Empenho autorizado! Avançando para Pagamento.');
        onSuccess();
      } else {
        throw new Error(response.message || 'Erro ao autorizar');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao autorizar empenho');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNegar = async () => {
    if (!motivo.trim() || motivo.trim().length < 5) {
      toast.error('Informe um motivo com pelo menos 5 caracteres');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiService.negarEmpenho({
        id_empenho: empenho.id,
        motivo: motivo.trim(),
      });
      if (response.success) {
        toast.success('Solicitação negada. Empenho retornado para Compras.');
        onSuccess();
      } else {
        throw new Error(response.message || 'Erro ao negar');
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao negar empenho');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const itensSC = solicitacao?.itens ?? [];
  const itensComMargem = itensSC.map((item: any) => {
    const valorProposta = Number(item.valor_venda_unitario ?? 0);
    const custo = Number(item.valor_custo_unitario ?? 0);
    const margem = calcularMargem(valorProposta, custo);
    return { ...item, valorProposta, custo, margem };
  });
  const algumAbaixo = itensComMargem.some(i => i.custo > 0 && i.margem < MARGEM_MINIMA);

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
              <h5 className="modal-title text-dark">
                <i className="ti ti-shield-check me-2 text-primary" />
                Aprovação de Margem — {empenho.codigo_empenho}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={isSubmitting} />
            </div>

            <div className="modal-body">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status" />
                  <p className="text-muted mt-2">Carregando detalhes...</p>
                </div>
              ) : (
                <>
                  {/* ── Alerta geral de margem ── */}
                  {algumAbaixo && (
                    <div className="alert alert-danger d-flex align-items-start gap-2 mb-4">
                      <i className="ti ti-alert-triangle fs-4 flex-shrink-0 mt-1" />
                      <div>
                        <strong>Atenção:</strong> Um ou mais produtos estão com margem abaixo de {MARGEM_MINIMA}%.
                        Revise os valores antes de autorizar.
                      </div>
                    </div>
                  )}

                  {/* ── 1. Análise de Margens ── */}
                  <div className="card mb-4">
                    <div className="card-header fw-bold">
                      <i className="ti ti-chart-bar me-2" />
                      Análise de Margens (Imposto fixo: 9%)
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-sm table-hover mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Produto</th>
                              <th className="text-end">Qtd Empenho</th>
                              <th className="text-end">Qtd Compra</th>
                              <th className="text-end">Vlr Proposta</th>
                              <th className="text-end">Custo Unit.</th>
                              <th className="text-end">Imposto (9%)</th>
                              <th className="text-end">Margem %</th>
                              <th className="text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itensComMargem.map((item: any, idx: number) => {
                              const imposto = item.valorProposta * IMPOSTO_FIXO;
                              const margemOk = item.custo > 0 && item.margem >= MARGEM_MINIMA;
                              const margemCor = item.custo <= 0
                                ? 'text-muted'
                                : item.margem >= MARGEM_MINIMA
                                  ? 'text-success fw-bold'
                                  : item.margem >= 8
                                    ? 'text-warning fw-bold'
                                    : 'text-danger fw-bold';
                              return (
                                <tr key={idx} className={item.custo > 0 && item.margem < MARGEM_MINIMA ? 'table-danger' : ''}>
                                  <td>{item.produto_nome || `Produto #${item.id_produto}`}</td>
                                  <td className="text-end">{item.quantidade_empenho != null ? Number(item.quantidade_empenho) : '-'}</td>
                                  <td className="text-end">{Number(item.quantidade)}</td>
                                  <td className="text-end">{formatarValor(item.valorProposta)}</td>
                                  <td className="text-end">{item.custo > 0 ? formatarValor(item.custo) : <span className="text-muted">—</span>}</td>
                                  <td className="text-end">{item.valorProposta > 0 ? formatarValor(imposto) : '—'}</td>
                                  <td className={`text-end ${margemCor}`}>
                                    {item.custo > 0 ? `${item.margem.toFixed(1)}%` : '—'}
                                  </td>
                                  <td className="text-center">
                                    {item.custo <= 0 ? (
                                      <span className="text-muted small">sem custo</span>
                                    ) : margemOk ? (
                                      <span className="badge badge-soft-success">
                                        <i className="ti ti-check me-1" />OK
                                      </span>
                                    ) : (
                                      <span className="badge badge-soft-danger">
                                        <i className="ti ti-alert-triangle me-1" />Abaixo
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* ── 2. Fornecedor e Dados Bancários ── */}
                  {solicitacao && (
                    <div className="card mb-4">
                      <div className="card-header fw-bold">
                        <i className="ti ti-building me-2" />
                        Fornecedor e Dados Bancários
                      </div>
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label fw-bold">Fornecedor</label>
                            <p className="form-control-plaintext">
                              {solicitacao.fornecedor_nome || solicitacao.fornecedor_razao || `Fornecedor #${solicitacao.id_fornecedor}`}
                            </p>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold">Condição de Pagamento</label>
                            <p className="form-control-plaintext">
                              {solicitacao.condicao_pagamento === 'A_VISTA' ? 'À Vista' : 'A Prazo'}
                            </p>
                          </div>
                        </div>
                        {solicitacao.dados_bancarios && (
                          <div className="row g-3 mt-1">
                            {solicitacao.dados_bancarios.tipo !== 'PIX' ? (
                              <>
                                <div className="col-md-3">
                                  <label className="form-label fw-bold">Banco</label>
                                  <p className="form-control-plaintext">{solicitacao.dados_bancarios.banco}</p>
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label fw-bold">Agência</label>
                                  <p className="form-control-plaintext">
                                    {solicitacao.dados_bancarios.agencia}
                                    {solicitacao.dados_bancarios.agencia_digito ? `-${solicitacao.dados_bancarios.agencia_digito}` : ''}
                                  </p>
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label fw-bold">Conta</label>
                                  <p className="form-control-plaintext">
                                    {solicitacao.dados_bancarios.conta}
                                    {solicitacao.dados_bancarios.conta_digito ? `-${solicitacao.dados_bancarios.conta_digito}` : ''}
                                  </p>
                                </div>
                                <div className="col-md-3">
                                  <label className="form-label fw-bold">Titular</label>
                                  <p className="form-control-plaintext">{solicitacao.dados_bancarios.titular}</p>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="col-md-4">
                                  <label className="form-label fw-bold">Tipo PIX</label>
                                  <p className="form-control-plaintext">{solicitacao.dados_bancarios.pix_tipo_chave}</p>
                                </div>
                                <div className="col-md-4">
                                  <label className="form-label fw-bold">Chave PIX</label>
                                  <p className="form-control-plaintext">{solicitacao.dados_bancarios.pix_chave}</p>
                                </div>
                                <div className="col-md-4">
                                  <label className="form-label fw-bold">Titular</label>
                                  <p className="form-control-plaintext">{solicitacao.dados_bancarios.titular}</p>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* ── 3. Parcelas / Condição ── */}
                  {parcelas.length > 0 && (
                    <div className="card mb-4">
                      <div className="card-header fw-bold">
                        <i className="ti ti-calendar-stats me-2" />
                        Parcelas Propostas
                      </div>
                      <div className="card-body">
                        <table className="table table-sm">
                          <thead>
                            <tr>
                              <th>Parcela</th>
                              <th className="text-end">Valor</th>
                              <th>Vencimento</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parcelas.map((p: any, i: number) => (
                              <tr key={i}>
                                <td><span className="badge bg-secondary">{p.numero_parcela}/{parcelas.length}</span></td>
                                <td className="text-end fw-bold">{formatarValor(p.valor)}</td>
                                <td>{p.data_vencimento ? formatarData(p.data_vencimento) : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="table-active fw-bold">
                              <td>Total:</td>
                              <td className="text-end">{formatarValor(parcelas.reduce((s: number, p: any) => s + Number(p.valor), 0))}</td>
                              <td />
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* ── Área de ação: Negar expande campo de motivo ── */}
                  {modo === 'negar' && (
                    <div className="card border-danger mb-4">
                      <div className="card-body">
                        <label className="form-label fw-bold text-danger">
                          <i className="ti ti-x me-1" />
                          Motivo da Negação <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={motivo}
                          onChange={(e) => setMotivo(e.target.value)}
                          placeholder="Descreva o motivo da negação (mín. 5 caracteres)..."
                          disabled={isSubmitting}
                          autoFocus
                        />
                        <div className="form-text">
                          O empenho voltará para Compras e a solicitação ficará marcada como NEGADA.
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer justify-content-between">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting || isLoading}>
                Cancelar
              </button>

              {!isLoading && (
                <div className="d-flex gap-2">
                  {/* Botão Negar */}
                  {modo !== 'autorizar' && (
                    modo === 'negar' ? (
                      <>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => setModo(null)} disabled={isSubmitting}>
                          Voltar
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger"
                          onClick={handleNegar}
                          disabled={isSubmitting || motivo.trim().length < 5}
                        >
                          {isSubmitting ? (
                            <><span className="spinner-border spinner-border-sm me-2" />Negando...</>
                          ) : (
                            <><i className="ti ti-x me-2" />Confirmar Negação</>
                          )}
                        </button>
                      </>
                    ) : (
                      <button type="button" className="btn btn-outline-danger" onClick={() => setModo('negar')} disabled={isSubmitting}>
                        <i className="ti ti-x me-2" />Negar
                      </button>
                    )
                  )}

                  {/* Botão Autorizar */}
                  {modo !== 'negar' && (
                    modo === 'autorizar' ? (
                      <>
                        <button type="button" className="btn btn-outline-secondary" onClick={() => setModo(null)} disabled={isSubmitting}>
                          Voltar
                        </button>
                        <button type="button" className="btn btn-success" onClick={handleAutorizar} disabled={isSubmitting}>
                          {isSubmitting ? (
                            <><span className="spinner-border spinner-border-sm me-2" />Autorizando...</>
                          ) : (
                            <><i className="ti ti-check me-2" />Confirmar Autorização</>
                          )}
                        </button>
                      </>
                    ) : (
                      <button type="button" className="btn btn-success" onClick={() => setModo('autorizar')} disabled={isSubmitting}>
                        <i className="ti ti-check me-2" />Autorizar
                      </button>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={!isSubmitting ? onClose : undefined} />
    </>
  );
};

export default FormAutorizarEmpenho;
